import type { Setlista, Utwor, UtworId } from '../store/db.ts';

// Ekran przygotowania setlisty (etap 8 planu): nazwa, kolejność utworów do
// grania, dodawanie i usuwanie pozycji. TO JEST EKRAN PRZYGOTOWANIA, NIE
// GRANIA - wolno tu przewijać, jak na ekranie wklejania (`ui/wklejanie.ts`).
//
// Kolejność zmienia się WYŁĄCZNIE przyciskami `▲`/`▼`, nigdy przeciąganiem:
// przeciąganie na telefonie wymaga celności i drugiej ręki, a jedna z nich
// trzyma gitarę przy ognisku.
//
// Każda zmiana (nazwa, kolejność, dodanie, usunięcie pozycji) zapisuje się
// NATYCHMIAST przez `naZapisz` - nie ma tu osobnego przycisku "zapisz": stan
// na ekranie ma być zawsze tym samym stanem, co w bazie. `gotowe` tylko
// zamyka ekran, nigdy nie jest jedyną okazją do zapisania zmian.

export interface OpcjeEkranuSetlisty {
  /** `null` = tworzenie nowej setlisty, jeszcze niezapisanej w bazie. */
  setlista: Setlista | null;
  /** WSZYSTKIE utwory w bazie, niezależnie od aktualnie wybranej setlisty -
   * sekcja "dodaj" pokazuje te spośród nich, których nie ma jeszcze tutaj. */
  wszystkieUtwory: Utwor[];
  naZapisz: (setlista: Setlista) => void | Promise<void>;
  naUsun: (id: string) => void | Promise<void>;
  naPowrot: () => void;
}

function nowyId(): string {
  // Ten sam wzorzec co `nowyId()` w `ui/wklejanie.ts`: opieranie jednego
  // identyfikatora o wsparcie przeglądarki dla `crypto.randomUUID()` nie jest
  // tego warte.
  return `setlista:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Zamienia miejscami dwie pozycje w tablicy identyfikatorów, po polsku
 * ostrożnie wobec `noUncheckedIndexedAccess`: bez tej strażniczej ścieżki
 * indeksy poza zakresem cicho wpisałyby `undefined` w miejsce identyfikatora.
 * Eksportowana (bez zmiany zachowania tego ekranu) - warsztat (`src/warsztat/`)
 * używa DOKŁADNIE tej samej funkcji do przycisków ▲/▼ swojego panelu setlist,
 * żeby nie duplikować tej samej logiki pod inną nazwą. */
export function zamien(tablica: UtworId[], a: number, b: number): void {
  const wartoscA = tablica[a];
  const wartoscB = tablica[b];
  if (wartoscA === undefined || wartoscB === undefined) return;
  tablica[a] = wartoscB;
  tablica[b] = wartoscA;
}

export function mountujEkranSetlisty(kontener: HTMLElement, deps: OpcjeEkranuSetlisty): void {
  // Kopia, nie referencja na `deps.setlista`: zmiany tutaj nie mają prawa
  // dotknąć cudzego obiektu przed potwierdzonym zapisem.
  const stan: Setlista = deps.setlista
    ? { ...deps.setlista, utwory: [...deps.setlista.utwory] }
    : { id: nowyId(), nazwa: '', utwory: [], utworzonaO: new Date().toISOString() };

  // Setlista tworzona przez "+ setlista" nie istnieje jeszcze w bazie -
  // "usuń setlistę" przed pierwszym zapisem nie ma czego usuwać, więc tylko
  // wraca do menu bez wołania `naUsun`.
  let zapisanaWBazie = deps.setlista !== null;

  const mapaUtworow = new Map(deps.wszystkieUtwory.map((u) => [u.id, u] as const));

  kontener.replaceChildren();
  kontener.className = 'zar-setlista-ekran';

  const naglowek = document.createElement('header');
  naglowek.className = 'zar-naglowek zar-naglowek--male';
  naglowek.innerHTML = '<h1>setlista</h1>';
  kontener.appendChild(naglowek);

  const cialo = document.createElement('div');
  cialo.className = 'zar-form';
  kontener.appendChild(cialo);

  const poleNazwa = document.createElement('label');
  poleNazwa.className = 'zar-pole';
  poleNazwa.innerHTML = '<span>Nazwa</span><input name="nazwa" type="text" autocomplete="off" />';
  const wejscieNazwa = poleNazwa.querySelector('input') as HTMLInputElement;
  wejscieNazwa.value = stan.nazwa;
  cialo.appendChild(poleNazwa);

  const sekcjaWybrane = document.createElement('div');
  sekcjaWybrane.className = 'zar-setlista-sekcja';
  sekcjaWybrane.innerHTML = '<p class="zar-setlista-etykieta">kolejność grania</p>';
  const listaWybrane = document.createElement('div');
  listaWybrane.className = 'zar-setlista-lista';
  listaWybrane.dataset.rola = 'wybrane';
  sekcjaWybrane.appendChild(listaWybrane);
  cialo.appendChild(sekcjaWybrane);

  const sekcjaDostepne = document.createElement('div');
  sekcjaDostepne.className = 'zar-setlista-sekcja';
  sekcjaDostepne.innerHTML = '<p class="zar-setlista-etykieta">dodaj z całego śpiewnika</p>';
  const listaDostepne = document.createElement('div');
  listaDostepne.className = 'zar-setlista-lista';
  listaDostepne.dataset.rola = 'dostepne';
  sekcjaDostepne.appendChild(listaDostepne);
  cialo.appendChild(sekcjaDostepne);

  const akcje = document.createElement('div');
  akcje.className = 'zar-akcje';
  akcje.innerHTML = '<button type="button" class="zar-przycisk zar-przycisk--niebezpieczny" data-akcja="usun-setliste">usuń setlistę</button>'
    + '<button type="button" class="zar-przycisk zar-przycisk--glowny" data-akcja="gotowe">gotowe</button>';
  cialo.appendChild(akcje);

  /** Zapisuje aktualny `stan` (kopią tablicy `utwory`, ten sam powód co przy
   * kopii wejściowej - `naZapisz` nie ma dostać referencji, którą ten moduł
   * mutuje chwilę później). Ustawia `zapisanaWBazie` na `true`: od pierwszego
   * zapisu "+ setlista" przestaje być rozróżnialne od edycji istniejącej. */
  function zapisz(): void {
    zapisanaWBazie = true;
    void deps.naZapisz({ ...stan, utwory: [...stan.utwory] });
  }

  function renderujListy(): void {
    listaWybrane.replaceChildren();
    if (stan.utwory.length === 0) {
      const pusta = document.createElement('p');
      pusta.className = 'zar-setlista-pusta';
      pusta.textContent = 'jeszcze pusto - dodaj utwory poniżej';
      listaWybrane.appendChild(pusta);
    }
    stan.utwory.forEach((id, i) => {
      const utwor = mapaUtworow.get(id);
      const wiersz = document.createElement('div');
      wiersz.className = 'zar-setlista-wiersz';

      const tyt = document.createElement('span');
      tyt.className = 'zar-setlista-tytul';
      if (utwor) {
        tyt.textContent = utwor.tytul;
      } else {
        // Utwór skasowany po dodaniu do tej setlisty - NIE kasujemy pozycji
        // po cichu (patrz `plik.ts`/spec), tylko pokazujemy ją jako brakującą.
        tyt.textContent = 'brakujący utwór';
        tyt.classList.add('zar-setlista-brakujacy');
      }
      wiersz.appendChild(opisUtworu(tyt, utwor?.wykonawca ?? null));

      const gora = document.createElement('button');
      gora.type = 'button';
      gora.className = 'zar-setlista-guzik';
      gora.textContent = '▲';
      gora.disabled = i === 0;
      gora.setAttribute('aria-label', `przesuń "${utwor?.tytul ?? 'brakujący utwór'}" wyżej`);
      gora.addEventListener('click', (z) => {
        z.stopPropagation();
        zamien(stan.utwory, i, i - 1);
        zapisz();
        renderujListy();
      });

      const dol = document.createElement('button');
      dol.type = 'button';
      dol.className = 'zar-setlista-guzik';
      dol.textContent = '▼';
      dol.disabled = i === stan.utwory.length - 1;
      dol.setAttribute('aria-label', `przesuń "${utwor?.tytul ?? 'brakujący utwór'}" niżej`);
      dol.addEventListener('click', (z) => {
        z.stopPropagation();
        zamien(stan.utwory, i, i + 1);
        zapisz();
        renderujListy();
      });

      const usun = document.createElement('button');
      usun.type = 'button';
      usun.className = 'zar-setlista-guzik zar-setlista-guzik--usun';
      usun.textContent = 'usuń';
      usun.setAttribute('aria-label', `usuń "${utwor?.tytul ?? 'brakujący utwór'}" z setlisty`);
      usun.addEventListener('click', (z) => {
        z.stopPropagation();
        stan.utwory.splice(i, 1);
        zapisz();
        renderujListy();
      });

      wiersz.append(gora, dol, usun);
      listaWybrane.appendChild(wiersz);
    });

    listaDostepne.replaceChildren();
    const wZbiorze = new Set(stan.utwory);
    const dostepne = deps.wszystkieUtwory.filter((u) => !wZbiorze.has(u.id));
    if (dostepne.length === 0) {
      const pusta = document.createElement('p');
      pusta.className = 'zar-setlista-pusta';
      pusta.textContent = 'wszystkie utwory są już w tej setliście';
      listaDostepne.appendChild(pusta);
    }
    dostepne.forEach((utwor) => {
      const wiersz = document.createElement('div');
      wiersz.className = 'zar-setlista-wiersz';
      const tyt = document.createElement('span');
      tyt.className = 'zar-setlista-tytul';
      tyt.textContent = utwor.tytul;
      wiersz.appendChild(opisUtworu(tyt, utwor.wykonawca));
      const dodaj = document.createElement('button');
      dodaj.type = 'button';
      dodaj.className = 'zar-setlista-guzik';
      dodaj.textContent = '+';
      dodaj.setAttribute('aria-label', `dodaj "${utwor.tytul}" do setlisty`);
      dodaj.addEventListener('click', (z) => {
        z.stopPropagation();
        stan.utwory.push(utwor.id);
        zapisz();
        renderujListy();
      });
      wiersz.append(dodaj);
      listaDostepne.appendChild(wiersz);
    });
  }

  renderujListy();

  wejscieNazwa.addEventListener('input', () => {
    stan.nazwa = wejscieNazwa.value;
    zapisz();
  });

  akcje.querySelector('[data-akcja="gotowe"]')?.addEventListener('click', () => deps.naPowrot());

  akcje.querySelector('[data-akcja="usun-setliste"]')?.addEventListener('click', () => {
    if (!zapisanaWBazie) { deps.naPowrot(); return; }
    // Jedno potwierdzenie zawsze - ten sam wymóg co przy usuwaniu utworu w
    // `ui/wklejanie.ts`. Zdanie w potwierdzeniu mówi wprost, że kasuje się
    // TYLKO listę, nie utwory, bo to jest dokładnie ten błąd, którego łatwo
    // się tu obawiać.
    if (!confirm(`Usunąć setlistę "${stan.nazwa || '(bez nazwy)'}"? Utwory w śpiewniku zostają, kasuje się tylko ta lista.`)) return;
    void deps.naUsun(stan.id);
  });
}

/** Tytuł z wykonawcą pod spodem, jak na liście w menu. Tytuł zostaje osobnym
 * elementem, bo jego tekst czytają testy i etykiety przycisków. */
function opisUtworu(tyt: HTMLElement, wykonawca: string | null): HTMLElement {
  const opis = document.createElement('div');
  opis.className = 'zar-setlista-opis';
  opis.appendChild(tyt);
  if (wykonawca) {
    const kto = document.createElement('span');
    kto.className = 'zar-setlista-wykonawca';
    kto.textContent = wykonawca;
    opis.appendChild(kto);
  }
  return opis;
}
